/*Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/


import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'
import { isEqual } from 'lodash'
import * as Sentry from '@sentry/browser'

import {
  SvgIcon,
  Table,
  Spinner,
  Button,
  Modal,
  SpanWithData,
  PageNavigationButtons,
  EmailDisplay
} from '../'

import {
  decodeBase64,
  PageNavigationControl,
  getPageCursor
} from '../../utils'

class Emails extends React.Component {
  static propTypes = {
    status: PropTypes.string,
    direction: PropTypes.string,
    emailType: PropTypes.string,
    fromAddr: PropTypes.string,
    fromIdentityId: PropTypes.string,
    toAddr: PropTypes.string,
    scheduledAtFrom: PropTypes.string,
    scheduledAtTo: PropTypes.string,
    createdAtFrom: PropTypes.string,
    createdAtTo: PropTypes.string,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func,
    resultsPerPage: PropTypes.number,
    className: PropTypes.string
  }

  static defaultProps = {
    resultsPerPage: 15
  }

  constructor(props) {
    super(props)

    this.state = {
      selectedEmail: null,
      replyToEmail: false,
      filterChanged: false,
      pageNav: new PageNavigationControl()
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!nextProps.loading) {
      if ((this.props.status !== nextProps.status ||
        this.props.direction !== nextProps.direction ||
        this.props.emailType !== nextProps.emailType ||
        this.props.fromAddr !== nextProps.fromAddr ||
        this.props.toAddr !== nextProps.toAddr ||
        this.props.scheduledAtFromDate !== nextProps.scheduledAtFromDate ||
        this.props.scheduledAtToDate !== nextProps.scheduledAtToDate ||
        this.props.createdAtFromDate !== nextProps.createdAtFromDate ||
        this.props.createdAtToDate !== nextProps.createdAtToDate ||
        this.props.orderBy !== nextProps.orderBy) &&
        !sessionStorage.getItem('emailListPageState')
      ) {
        let previousFilters = JSON.parse(sessionStorage.getItem('emailListFilters'))
        if (previousFilters && (
          previousFilters.status === nextProps.status ||
          previousFilters.direction === nextProps.direction ||
          previousFilters.emailType === nextProps.emailType ||
          previousFilters.fromAddr === nextProps.fromAddr ||
          previousFilters.toAddr === nextProps.toAddr ||
          previousFilters.scheduledAtFromDate === nextProps.scheduledAtFromDate ||
          previousFilters.scheduledAtToDate === nextProps.scheduledAtToDate ||
          previousFilters.createdAtFromDate === nextProps.createdAtFromDate ||
          previousFilters.createdAtToDate === nextProps.createdAtToDate ||
          previousFilters.orderBy === nextProps.orderBy
        )) {
          sessionStorage.removeItem('emailListFilters')
        } else {
          this.state.pageNav.reset()
          this.setState({ filterChanged: true }, () => this.props.refetch())
          sessionStorage.removeItem('emailListFilters')
        }
      } else {
        let emails = this.props.toIdentityId
          ? nextProps.identity.emailsTo
          : nextProps.emails
        let nextEmails = this.props.toIdentityId
          ? nextProps.identity.emailsTo
          : nextProps.emails
        if (this.state.filterChanged) {
          this.setState({ filterChanged: false })
          this.state.pageNav.setPageStates(null, nextEmails)
        } else if (nextEmails) {
          let emailListPageState = JSON.parse(sessionStorage.getItem('emailListPageState'))
          let sameResultsAsBefore = isEqual(emails, nextEmails)
          if (emailListPageState) {
            this.state.pageNav.setPageStates(null, null, emailListPageState)
            sessionStorage.removeItem('emailListPageState')
            this.props.refetch({ cursor: emailListPageState.cursor })
          } else if (!sameResultsAsBefore) {
            this.state.pageNav.setPageStates(emails, nextEmails)
          } else if (sameResultsAsBefore) {
            this.state.pageNav.setPageStates(null, nextEmails)
          }
          else {
            Sentry.captureMessage('Email listing pagination button state logic exception')
          }
        }
      }
    }
  }

  getTableRows = (emails) => (
    emails.map(({ node: email }) => (
      <EmailTableItem
        email={email}
        identity={this.props.identity}
        actionColumn={this.props.actionColumn}
        expandEmail={this.state.selectedEmail === email.id}
        messageIdentity={email.direction === 'INBOUND' && email.fromIdentity !== null
          ? email.fromIdentity
          : email.direction === 'OUTBOUND' && email.toIdentity !== null
            ? email.toIdentity
            : null
        }
        onExpandClick={emailId => this.setState({ selectedEmail: emailId })}
        onExpandCloseClick={() => this.setState({ selectedEmail: null })}
        key={`email_${email.id}`} />
    ))
  )

  render() {
    if (this.props.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    let pageNav = this.state.pageNav
    let columns = ['Identity', 'Direction', 'Status', 'Date/Time', 'Type', 'Message']
    if (this.props.actionColumn) {
      columns.push(this.props.actionColumn.name)
    }

    let emails = this.props.toIdentityId
      ? this.props.identity.emailsTo
      : this.props.emails

    let startCursor = emails.pageInfo.startCursor
    let totalCount = emails.totalCount

    return (
      <div className={this.props.className ? this.props.className : ''}>
        {emails.edges.length === 0
          ? <div className='slds-p-around_medium'>
              <SvgIcon
                variant='utility'
                symbol='info'
                size='small'
                colour='light'
                className='slds-m-right_medium'
              />
              {`No ${this.props.direction} emails.`}`
            </div>
          : <React.Fragment>
              <Table
                columns={columns}
                rows={this.getTableRows(emails.edges)}
                objectRows={true}
                orderBy={this.props.orderBy}
                changeSort={this.props.changeSort}
                querySortableColumns={{
                  'Date/Time': {
                    orderByName: 'created_at'
                  }
                }}
              />
              <PageNavigationButtons
                onNextClick={() => pageNav.nextPage(this.props.fetchNext)}
                onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious)}
                previousDisabled={pageNav.firstPage}
                nextDisabled={pageNav.lastPage}
                startCursor={startCursor}
                totalCount={totalCount}
                resultsPerPage={this.props.resultsPerPage}
                resultsText='emails'
                onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.resultsPerPage) })}
                className="slds-p-around_medium slds-float_right"
              />
            </React.Fragment>
        }
      </div>
    )
  }
}

const EmailTableItem = ({ email, identity, actionColumn, expandEmail, replyToEmail, messageIdentity, onExpandClick, onExpandCloseClick }) => {
  const messageHasContentToView = email.textBody || email.attachments.length > 0

  return (
    <tr onClick={() => messageHasContentToView ? onExpandClick(email.id) : false} className={messageHasContentToView ? 'clickable' : ''}>
      <td>
        {messageIdentity
          ? messageIdentity.fullName
          : 'Unknown'
        }
      </td>
      <td>
        <SpanWithData forField="EmailDirection" searchKey={email.direction} />
      </td>
      <td>
        <SpanWithData forField="EmailStatus" searchKey={email.status} />
      </td>
      <td>
        {moment().isSame(moment(email.createdAt), 'day')
          ? moment(email.createdAt).format('hh:mm a')
          : moment(email.createdAt).format('lll')
        }
      </td>
      <td>
        {email.emailType
          ? <SpanWithData forField="EmailEmailType" searchKey={email.emailType} />
          : "Unknown"
        }
      </td>
      <td>
        {expandEmail
          ? <Modal
              body={
                <EmailDisplay
                  subject={email.subject}
                  fromAddr={email.fromAddr}
                  toAddrs={email.toAddrs}
                  textBody={email.textBody}
                  htmlBody={email.htmlBody}
                  attachments={email.attachments}
                />
              }
              footer={
                <Button
                  type='brand'
                  onClick={(e) => {
                    e.stopPropagation()  // prevents click on outer <tr> to be captured
                    onExpandCloseClick()
                  }}
                  className='slds-align_absolute-center'>
                  Close
                </Button>
              }
            />
          : <div
              className='slds-truncate'
              style={{ width: '200px' }}>
              {email.textBody}
            </div>
        }
      </td>
      {actionColumn &&
        <td>
          {messageIdentity !== null
            ? actionColumn.render(email.id, messageIdentity.id)
            : '-'
          }
        </td>
      }
    </tr>
  )
}

const getEmails = gql`
  query($status: String, $direction: String, $emailType: String, $fromAddr: String, $fromIdentityId: UUID, $toIdentityId: ID!, $createdAtFrom: DateTime, $createdAtTo: DateTime, $scheduledAtFrom: DateTime, $scheduledAtTo: DateTime, $resultsPerPage: Int!, $orderBy: String, $cursor: String, $toIdentityIdProvided: Boolean!) {
    emails(status: $status, direction: $direction, emailType: $emailType, fromAddr_Icontains: $fromAddr, fromIdentity_Id: $fromIdentityId, createdAt_Gte: $createdAtFrom, createdAt_Lte: $createdAtTo, scheduledAt_Gte: $scheduledAtFrom, scheduledAt_Lte: $scheduledAtTo, orderBy: $orderBy, first: $resultsPerPage, after: $cursor) @skip(if: $toIdentityIdProvided) {
      totalCount
      edges {
        node {
          ...Email
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
    identity(id: $toIdentityId) @include(if: $toIdentityIdProvided) {
      id
      emailsTo(status: $status, emailType: $emailType, fromAddr_Icontains: $fromAddr, createdAt_Gte: $createdAtFrom, createdAt_Lte: $createdAtTo, scheduledAt_Gte: $scheduledAtFrom, scheduledAt_Lte: $scheduledAtTo, first: $resultsPerPage, after: $cursor) {
        edges {
          node {
            ...Email
          }
        }
        pageInfo {
          endCursor
          startCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
  ${fragments.email}
`

const getPreviousEmails = gql`
  query($status: String, $direction: String, $emailType: String, $fromAddr: String, $fromIdentityId: UUID, $toIdentityId: ID!, $createdAtFrom: DateTime, $createdAtTo: DateTime, $scheduledAtFrom: DateTime, $scheduledAtTo: DateTime, $resultsPerPage: Int!, $orderBy: String, $cursor: String, $toIdentityIdProvided: Boolean!) {
    emails(status: $status, direction: $direction, emailType: $emailType, fromAddr_Icontains: $fromAddr, fromIdentity_Id: $fromIdentityId, createdAt_Gte: $createdAtFrom, createdAt_Lte: $createdAtTo, scheduledAt_Gte: $scheduledAtFrom, scheduledAt_Lte: $scheduledAtTo, orderBy: $orderBy, last: $resultsPerPage, before: $cursor) @skip(if: $toIdentityIdProvided) {
      totalCount
      edges {
        node {
          ...Email
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
    identity(id: $toIdentityId) @include(if: $toIdentityIdProvided) {
      id
      emailsTo(status: $status, emailType: $emailType, fromAddr_Icontains: $fromAddr, createdAt_Gte: $createdAtFrom, createdAt_Lte: $createdAtTo, scheduledAt_Gte: $scheduledAtFrom, scheduledAt_Lte: $scheduledAtTo, last: $resultsPerPage, before: $cursor) {
        edges {
          node {
            ...Email
          }
        }
        pageInfo {
          endCursor
          startCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
  ${fragments.email}
`

const withData = graphql(getEmails, {
  options: ({ status, direction, emailType, fromAddr, fromIdentityId, toIdentityId, createdAtFrom, createdAtTo, scheduledAtFrom, scheduledAtTo, resultsPerPage = 15, orderBy }) => ({
    variables: {
      status,
      direction,
      emailType,
      fromAddr,
      fromIdentityId: fromIdentityId ? decodeBase64(fromIdentityId) : null,
      toIdentityId: toIdentityId || 'SWRlbnRpdHlOb2RlOmQ0ZWUyM2UwLTdlYzUtNDdiYy04ODM5LWNlMzdlZWZlZjMzMQ==',  // Picsa Advisory Services identity id; has not effect as toIdentityIdProvided determines part of query to run, necessary because identity part of query needs a parameter value passed in
      createdAtFrom,
      createdAtTo,
      scheduledAtFrom,
      scheduledAtTo,
      resultsPerPage,
      orderBy,
      toIdentityIdProvided: toIdentityId !== null && toIdentityId !== undefined
    },
    fetchPolicy: 'network-only'
  }),
  props: ({ ownProps: { status, direction, emailType, fromAddr, fromIdentityId, toIdentityId, createdAtFrom, createdAtTo, scheduledAtFrom, scheduledAtTo, resultsPerPage = 15, orderBy }, data: { loading, error, refetch, emails, identity, fetchMore } }) => ({
    loading,
    error,
    refetch,
    emails,
    identity,
    fetchNext: () => {
      return fetchMore({
        query: getEmails,
        variables: {
          status,
          direction,
          emailType,
          fromAddr,
          fromIdentityId: fromIdentityId ? decodeBase64(fromIdentityId) : null,
          toIdentityId: toIdentityId || 'SWRlbnRpdHlOb2RlOmQ0ZWUyM2UwLTdlYzUtNDdiYy04ODM5LWNlMzdlZWZlZjMzMQ==',
          createdAtFrom,
          createdAtTo,
          scheduledAtFrom,
          scheduledAtTo,
          resultsPerPage,
          orderBy,
          toIdentityIdProvided: toIdentityId !== null && toIdentityId !== undefined,
          cursor: toIdentityId
            ? identity.emailsTo.pageInfo.endCursor
            : emails.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (toIdentityId) {
            return {
              identity: {
                id: previousResult.identity.id,
                __typename: previousResult.identity.__typename,
                emailsTo: {
                  __typename: previousResult.identity.emailsTo.__typename,
                  edges: [...fetchMoreResult.identity.emailsTo.edges],
                  pageInfo: fetchMoreResult.identity.emailsTo.pageInfo
                }
              }
            }
          } else {
            return {
              emails: {
                __typename: previousResult.emails.__typename,
                edges: [...fetchMoreResult.emails.edges],
                pageInfo: fetchMoreResult.emails.pageInfo
              }
            }
          }
        }
      })
    },
    fetchPrevious: () => {
      return fetchMore({
        query: getPreviousEmails,
        variables: {
          status,
          direction,
          emailType,
          fromAddr,
          fromIdentityId: fromIdentityId ? decodeBase64(fromIdentityId) : null,
          toIdentityId: toIdentityId || 'SWRlbnRpdHlOb2RlOmQ0ZWUyM2UwLTdlYzUtNDdiYy04ODM5LWNlMzdlZWZlZjMzMQ==',
          createdAtFrom,
          createdAtTo,
          scheduledAtFrom,
          scheduledAtTo,
          resultsPerPage,
          orderBy,
          toIdentityIdProvided: toIdentityId !== null && toIdentityId !== undefined,
          cursor: toIdentityId
            ? identity.emailsTo.pageInfo.startCursor
            : emails.pageInfo.startCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (toIdentityId) {
            return {
              identity: {
                id: previousResult.identity.id,
                __typename: previousResult.identity.__typename,
                emailsTo: {
                  __typename: previousResult.identity.emailsTo.__typename,
                  edges: [...fetchMoreResult.identity.emailsTo.edges],
                  pageInfo: fetchMoreResult.identity.emailsTo.pageInfo
                }
              }
            }
          } else {
            return {
              emails: {
                __typename: previousResult.emails.__typename,
                edges: [...fetchMoreResult.emails.edges],
                pageInfo: fetchMoreResult.emails.pageInfo
              }
            }
          }
        }
      })
    }
  })
})

export default withData(Emails)



// WEBPACK FOOTER //
// ./src/components/emails/index.js